import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'redux/hooks';
import {
  setPurposeId,
  setIsLoan,
  setMode,
  setCountry,
  setAmount,
  setFXCharges,
  setStudentName,
  setCustomer_rate,
  setAddOnRates,
  setCustomSettlementRate,
  setRemitterName,
  setRemitterPAN,
  setRemitterDOB,
  setPurposeIdCopy,
  setCurrency,
} from '../../../redux/features/CreateDeal';
import { useDealData } from './useDealData';
import { setBranch } from 'redux/features/CreateStaff';

export interface IFormInputMap {
  customSettlementRate: string;
  purpose: string;
  country: string;
  amount: number;
  charges: string;
  rate?: number;
  name?: string;
  servicePartner?: string;
  remitter_name?: string;
  pan?: string;
  dob?: string;
  mode?: string;
  isLoan?: boolean;
  branch?: string;
}

interface FormState {
  purpose: {
    id: number;
    label: string;
    requiresLoanSelection: boolean;
    requiresServiceProvider: boolean;
  } | null;
  hasLoan: boolean | null;
  serviceProvider: string | null;
}

export const useDealForm = (updateCurrencyForCountry: (country: string) => string, rate: number) => {
  const form = useForm<IFormInputMap>();
  const dispatch = useDispatch();
  const [activeField, setActiveField] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [formData, setFormData] = useState<any[]>([]);
  const { allPurpose } = useDealData();

  const { purpose_id, isLoan, mode } = useAppSelector((state) => state.CreateDeal);

  const purposeMap = {
    purposes: [
      {
        id: 1,
        label: "Education",
        requiresLoanSelection: true,
        requiresServiceProvider: true,
      },
      {
        id: 2,
        label: "GIC",
        requiresLoanSelection: true,
        requiresServiceProvider: false,
      },
      {
        id: 3,
        label: "Gift",
        requiresLoanSelection: false,
        requiresServiceProvider: false,
      },
      {
        id: 4,
        label: "Study Maintenance",
        requiresLoanSelection: false,
        requiresServiceProvider: false,
      },
    ],
  };

  const [formState, setFormState] = useState<FormState>({
    purpose: null,
    hasLoan: null,
    serviceProvider: null,
  });

  /**
   * The wire services education can be sent through. A purpose on one of
   * these is a variant of Education; anything else is a purpose in its own
   * right, and its mode is its own name.
   */
  const WIRE_MODES = [
    "flywire",
    "westernunion",
    "cibc",
    "paymytuition",
    "viatransfer",
  ];

  const modeOf = (p: any): string => String(p?.mode ?? "").toLowerCase();
  const isVariant = (p: any): boolean => WIRE_MODES.includes(modeOf(p));

  /**
   * The purpose actually being bought, out of the one the operator picked
   * plus the loan answer and the wire service.
   *
   * "Education with loan" exists once per wire service and GIC exists with
   * and without a loan, so the pair has to be resolved against the purpose
   * list. This used to hold Xano's ids inline -- GIC-with-loan was "14" --
   * and those ids are not ours: 14 here is Education Via Transfer with
   * loan, so a GIC deal was silently booked as an education one.
   */
  const purposeSetter = (id: string, isLoan: string, mode: string) => {
    const base = allPurpose?.find((p: any) => String(p.id) === String(id));
    if (!base) return;

    const loanBool = isLoan === "true";
    // Education is offered through several services, so its mode comes from
    // the dropdown. Everything else carries exactly one.
    const wanted = (modeOf(base) === "education" ? mode : base.mode) ?? "";
    const match = allPurpose?.find(
      (p: any) =>
        modeOf(p) === String(wanted).toLowerCase() &&
        Boolean(p.loan) === loanBool,
    );

    // No variant for this combination -- Study Maintenance has no loan
    // version -- so the purpose picked is the purpose meant.
    dispatch(setPurposeIdCopy(String(match?.id ?? base.id)));
  };

  /**
   * Which of the four choices in the dropdown a specific purpose sits under,
   * so an opportunity carrying "Education CIBC with loan" reopens the form
   * on Education with CIBC and the loan answer filled in.
   *
   * Worked out from the purpose list rather than a table of ids, which held
   * Xano's numbering and no longer matches ours.
   */
  const getBelongingBaseId = (purposeId: string) => {
    const picked = allPurpose?.find(
      (p: any) => String(p.id) === String(purposeId),
    );
    if (!picked) return String(purposeId);

    // A variant belongs to Education; anything else already is its own base,
    // except that GIC-with-loan sits under plain GIC.
    const baseMode = isVariant(picked) ? "education" : modeOf(picked);
    const base = allPurpose?.find(
      (p: any) => modeOf(p) === baseMode && !p.loan,
    );
    return String(base?.id ?? purposeId);
  };

  const handlePurposeChange = (purposeId: string) => {
    const selectedPurpose = purposeMap.purposes.find(
      (p) => p.id.toString() === purposeId
    );

    setFormState({
      purpose: selectedPurpose || null,
      hasLoan: null,
      serviceProvider: null,
    });

    // Reset dependent form fields
    form.setValue('isLoan', '');
    form.setValue('mode', '');
  };

  const shouldDisplayField = (fieldName: string) => {
    const { purpose } = formState;

    // if (purpose_id === "1" && mode === "ViaTransfer" && fieldName === "isLoan") {
    //   return purpose?.requiresLoanSelection === false;
    // }

    if (fieldName === "isLoan") {
      return purpose?.requiresLoanSelection === true;
    }

    if (fieldName === "mode") {
      return purpose?.requiresServiceProvider === true;
    }

    return true;
  };

  const getFormConfig = (currency: string, purpose: any[], branches: any[]) => {
    const baseConfig = [
      [

        {
          label: "What is your purpose for sending the money ?",
          name: "purpose",
          type: "select",
          placeholder: "Select a purpose for sending money",
          options: purpose,
        },
        {
          label: "Mode",
          name: "mode",
          type: "select",
          optional: true,
          alwaysShow: true,
          options: [
            { value: "Flywire", label: "Flywire" },
            { value: "WesternUnion", label: "Western Union" },
            { value: 'CIBC', label: 'CIBC' },
            { value: 'ViaTransfer', label: 'Via Transfer' },
            { value: 'PayMyTuition', label: 'Pay My Tuition' }
          ],
        },
        {
          label: "Have you taken loan?",
          name: "isLoan",
          type: "radio",
          optional: true,
          alwaysShow: true,
          options: [
            { value: "true", label: "YES" },
            { value: "false", label: "NO" },
          ],
        },
        {
          label: "Where do you want to send the money ?",
          name: "country",
          type: "select",
          placeholder: "Select country",
          options: [],
        },
        {
          label: "Enter the amount you want to send.",
          name: "amount",
          type: "currency",
          currency,
        },
        {
          label: "Select the Foreign Charges",
          name: "charges",
          type: "radio",
          optional: true,
          alwaysShow: true,
          options: [
            { value: "BEN", label: "BEN" },
            { value: "OUR", label: "OUR" },
          ],
        },
        {
          label: "Enter the customer rate (optional)",
          name: "rate",
          type: "currency",
          currency: "INR",
          optional: true,
        },
      ],
      [
        {
          label: "Custom Settlement Rate",
          name: "customSettlementRate",
          type: "settlement-rate-input",
          currency: "INR",
          optional: true,
          alwaysShow: true,
        },
        {
          label: "What is the name of student? (optional)",
          name: "name",
          type: "text",
          optional: true,
          placeholder: "John Doe",
          alwaysShow: true,
        },
      ],
    ];

    return baseConfig;
  };

  useEffect(() => {
    purposeSetter(purpose_id, isLoan, mode);
  }, [purpose_id, isLoan, mode]);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name !== undefined && value[name] !== undefined) {
        switch (name) {
          case "purpose":
            dispatch(setPurposeId(value[name]));
            handlePurposeChange(value[name]);
            break;
          case "isLoan":
            dispatch(setIsLoan(value[name]));
            break;
          case "mode":
            dispatch(setMode(value[name]));
            break;
          case "country":
            dispatch(setCountry(value[name]));
            const newCurrency = updateCurrencyForCountry(value[name]);
            dispatch(setCurrency(newCurrency));
            break;
          case "amount":
            if (value["amount"]) {
              form.setValue("charges", "BEN");
              setActiveField((prev) => prev + 1);
            }
            dispatch(setAmount(String(value[name])));
            break;
          case "charges":
            dispatch(setFXCharges(value[name]));
            break;
          case "name":
            dispatch(setStudentName(value[name]));
            break;
          case "customSettlementRate":
            // What we pay the service partner, agreed with them on the
            // phone. Entering it here is what confirms the deal, so it
            // goes into the quote too and the panel shows the price the
            // deal will actually be booked at.
            dispatch(setCustomSettlementRate(String(value[name] ?? "")));
            break;
          case "rate":
            dispatch(setCustomer_rate(value[name]?.toString() || ""));
            dispatch(
              setAddOnRates(
                parseFloat(String(value[name])) - parseFloat(rate.toFixed(4))
              )
            );
            break;
          case "remitter_name":
            dispatch(setRemitterName(value[name]));
            break;
          case "pan":
            dispatch(setRemitterPAN(value[name]));
            break;
          case "dob":
            dispatch(setRemitterDOB(value[name]));
            break;
        }

        // Update activeField if necessary
        const currentFieldIndex = formData.findIndex(
          (field) => field.name === name
        );
        if (currentFieldIndex === activeField && value[name]) {
          setActiveField((prev) => Math.min(prev + 1, formData.length - 1));
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form.watch, rate, updateCurrencyForCountry, activeField, formData]);

  return {
    form,
    activeField,
    setActiveField,
    currentStep,
    setCurrentStep,
    formData,
    setFormData,
    shouldDisplayField,
    getFormConfig,
    handlePurposeChange,
    purposeSetter,
    getBelongingBaseId
  };
};